import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { User } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"

function CoachCard({ coach, scheduleCallUrl }) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="p-6 text-center">
        {/* Headshot */}
        <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-zinc-100">
          {coach.headshotUrl ? (
            <img
              src={coach.headshotUrl}
              alt={coach.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-kf-gradient flex items-center justify-center">
              <span className="text-white text-3xl font-display">
                {coach.name?.charAt(0) || "K"}
              </span>
            </div>
          )}
        </div>

        {/* Name */}
        <h3 className="font-display text-xl text-zinc-900 mb-1">
          {coach.name}
        </h3>

        {/* Job & Company */}
        {(coach.jobTitle || coach.company) && (
          <p className="text-sm text-zinc-500 mb-3">
            {[coach.jobTitle, coach.company].filter(Boolean).join(" at ")}
          </p>
        )}

        {/* Bio */}
        {coach.bio && (
          <p className="text-zinc-600 text-sm mb-4 line-clamp-3">
            {coach.bio}
          </p>
        )}

        {/* Article count */}
        <p className="text-xs text-zinc-400 uppercase tracking-wider mb-4">
          {coach.articleCount} {coach.articleCount === 1 ? "article" : "articles"} published
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button variant="kf-outline" size="sm" asChild>
            {coach.profileArticleUrl ? (
              <Link href={coach.profileArticleUrl}>View Profile</Link>
            ) : (
              <Link href={`/collective/coaches/${coach.slug}`}>View Profile</Link>
            )}
          </Button>
          {coach.scheduleCallUrl && coach.scheduleCallUrl !== "#" && (
            <Button variant="gradient" size="sm" asChild>
              <a href={coach.scheduleCallUrl} target="_blank" rel="noopener noreferrer">Schedule a Call</a>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function CoachesIndex({ seo, coaches = [], settings = {} }) {
  const { scheduleCallUrl = "#" } = settings

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="collective" scheduleCallUrl={scheduleCallUrl} />

        <section className="bg-zinc-50 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                OUR COACHES
              </h1>
              <p className="text-zinc-600 text-lg max-w-2xl mx-auto">
                Faith-driven leaders dedicated to helping you integrate biblical principles
                into your business for lasting impact.
              </p>
            </div>

            {/* Coach Grid */}
            {coaches.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {coaches.map((coach) => (
                  <CoachCard
                    key={coach.id}
                    coach={coach}
                    scheduleCallUrl={scheduleCallUrl}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <User className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500">No coaches to display yet.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-kf-gradient py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
              READY TO TRANSFORM YOUR BUSINESS?
            </h2>
            <p className="text-white/80 mb-8">
              Join our community of faith-driven leaders and start your journey today.
            </p>
            {scheduleCallUrl && scheduleCallUrl !== "#" && (
              <Button variant="kf-secondary" asChild>
                <a href={scheduleCallUrl} target="_blank" rel="noopener noreferrer">Schedule a Call</a>
              </Button>
            )}
          </div>
        </section>

        <Footer activePage="collective" />
      </div>
    </>
  )
}
